import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarDays, DollarSign, Users, BedDouble, TrendingUp, TrendingDown, Hotel, Star, CheckCircle, UserCheck, Clock, AlertCircle, Bell, Calendar as CalendarIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import { BACKEND_URL } from '@/lib/backendUrl';

const PREVIEW_URL = window.location.origin;

// Date range presets
const DATE_PRESETS = [
  { label: 'Today', value: 'today', days: 0 },
  { label: 'Yesterday', value: 'yesterday', days: 1 },
  { label: 'Last 7 Days', value: 'last_7_days', days: 7 },
  { label: 'Last 30 Days', value: 'last_30_days', days: 30 },
  { label: 'This Month', value: 'this_month', days: null },
  { label: 'Last Month', value: 'last_month', days: null },
  { label: 'Custom Range', value: 'custom', days: null }
];

export
function ModernOverviewTab({ hotelId, hotelName, hotels }) {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState(null);
  const [requests, setRequests] = useState([]);
  const [dateRange, setDateRange] = useState('last_7_days');
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  useEffect(() => {
    loadData();
  }, [hotelId, dateRange, customDateRange]);

  const getDateRangeParams = () => {
    const today = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case 'today':
        startDate = endDate = format(today, 'yyyy-MM-dd');
        break;
      case 'yesterday':
        const yesterday = subDays(today, 1);
        startDate = endDate = format(yesterday, 'yyyy-MM-dd');
        break;
      case 'last_7_days':
        startDate = format(subDays(today, 7), 'yyyy-MM-dd');
        endDate = format(today, 'yyyy-MM-dd');
        break;
      case 'last_30_days':
        startDate = format(subDays(today, 30), 'yyyy-MM-dd');
        endDate = format(today, 'yyyy-MM-dd');
        break;
      case 'this_month':
        startDate = format(startOfMonth(today), 'yyyy-MM-dd');
        endDate = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'last_month':
        const lastMonth = subDays(startOfMonth(today), 1);
        startDate = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
        endDate = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
        break;
      case 'custom':
        if (customDateRange.from && customDateRange.to) {
          startDate = format(customDateRange.from, 'yyyy-MM-dd');
          endDate = format(customDateRange.to, 'yyyy-MM-dd');
        } else {
          // Default to last 7 days if custom not set
          startDate = format(subDays(today, 7), 'yyyy-MM-dd');
          endDate = format(today, 'yyyy-MM-dd');
        }
        break;
      default:
        startDate = format(subDays(today, 7), 'yyyy-MM-dd');
        endDate = format(today, 'yyyy-MM-dd');
    }

    return { start_date: startDate, end_date: endDate };
  };

  const getDateRangeLabel = () => {
    if (dateRange === 'custom' && customDateRange.from && customDateRange.to) {
      return `${format(customDateRange.from, 'MMM dd, yyyy')} - ${format(customDateRange.to, 'MMM dd, yyyy')}`;
    }
    const preset = DATE_PRESETS.find(p => p.value === dateRange);
    return preset ? preset.label : 'Last 7 Days';
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;
      const dateParams = getDateRangeParams();
      
      const [reportsRes, requestsRes] = await Promise.all([
        fetch(`${backendUrl}/api/pms/${hotelId}/reports?start_date=${dateParams.start_date}&end_date=${dateParams.end_date}`),
        fetch(`${backendUrl}/api/service-requests/${hotelId}`)
      ]);
      
      const reportsData = await reportsRes.json();
      const requestsData = await requestsRes.json();
      
      if (reportsData.success) setReports(reportsData);
      if (requestsData.success) setRequests(requestsData.requests || []);
    } catch (error) {
      console.error('Failed to load overview data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (value) => {
    if (value === 'custom') {
      setShowCustomPicker(true);
    } else {
      setDateRange(value);
      setShowCustomPicker(false);
    }
  };

  const handleCustomDateSelect = (range) => {
    if (range?.from && range?.to) {
      setCustomDateRange(range);
      setDateRange('custom');
      setShowCustomPicker(false);
      toast.success(`Date range updated: ${format(range.from, 'MMM dd')} - ${format(range.to, 'MMM dd')}`);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <Skeleton className="md:col-span-8 h-80" />
          <Skeleton className="md:col-span-4 h-80" />
        </div>
      </div>
    );
  }

  if (!reports) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">Unable to load dashboard data</p>
        </CardContent>
      </Card>
    );
  }

  const { current_status, bookings, revenue, occupancy } = reports;

  // Generate mock trend data for visualization
  const trendData = [
    { date: 'Mon', revenue: 1200, occupancy: 65 },
    { date: 'Tue', revenue: 1850, occupancy: 78 },
    { date: 'Wed', revenue: 2100, occupancy: 85 },
    { date: 'Thu', revenue: 1950, occupancy: 80 },
    { date: 'Fri', revenue: 2400, occupancy: 92 },
    { date: 'Sat', revenue: 2800, occupancy: 98 },
    { date: 'Sun', revenue: 2650, occupancy: 95 }
  ];

  const pendingRequests = requests.filter(r => ['pending', 'acknowledged', 'in_progress'].includes(r.status)).slice(0, 5);

  return (
    <div className="space-y-6" data-testid="owner-overview-tab">
      {/* Date Range Selector Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold heading-font tracking-tight">Performance Overview</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Viewing data for: <span className="font-semibold text-foreground">{getDateRangeLabel()}</span>
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={handleDateRangeChange} data-testid="date-range-selector">
            <SelectTrigger className="w-[200px]">
              <CalendarIcon className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((preset) => (
                <SelectItem key={preset.value} value={preset.value} data-testid={`date-preset-${preset.value}`}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {showCustomPicker && (
            <Popover open={showCustomPicker} onOpenChange={setShowCustomPicker}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[200px]" data-testid="custom-date-picker-trigger">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {customDateRange.from && customDateRange.to
                    ? `${format(customDateRange.from, 'MMM dd')} - ${format(customDateRange.to, 'MMM dd')}`
                    : 'Pick dates'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={customDateRange}
                  onSelect={handleCustomDateSelect}
                  numberOfMonths={2}
                  data-testid="custom-date-calendar"
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* 5 KPI Cards Strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="rounded-xl border bg-card shadow-[0_10px_30px_rgba(15,23,42,0.06)] hover:shadow-[0_16px_40px_rgba(15,23,42,0.10)] transition-shadow duration-200" data-testid="overview-kpi-bookings-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Bookings</p>
                <p className="text-2xl md:text-3xl heading-font tracking-tight tabular-nums" data-testid="overview-kpi-bookings-value">
                  {bookings.total}
                </p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs font-medium text-[hsl(var(--kpi-positive))]" data-testid="overview-kpi-bookings-delta">
                    ↑ +12%
                  </span>
                  <span className="text-xs text-muted-foreground">vs last period</span>
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <CalendarDays className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-[0_10px_30px_rgba(15,23,42,0.06)] hover:shadow-[0_16px_40px_rgba(15,23,42,0.10)] transition-shadow duration-200" data-testid="overview-kpi-revenue-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Revenue</p>
                <p className="text-2xl md:text-3xl heading-font tracking-tight tabular-nums" data-testid="overview-kpi-revenue-value">
                  ${revenue.total_revenue}
                </p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs font-medium text-[hsl(var(--kpi-positive))]" data-testid="overview-kpi-revenue-delta">
                    ↑ +8%
                  </span>
                  <span className="text-xs text-muted-foreground">vs last period</span>
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--status-clean))]/10 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-5 h-5 text-[hsl(var(--status-clean))]" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-[0_10px_30px_rgba(15,23,42,0.06)] hover:shadow-[0_16px_40px_rgba(15,23,42,0.10)] transition-shadow duration-200" data-testid="overview-kpi-rooms-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Rooms</p>
                <p className="text-2xl md:text-3xl heading-font tracking-tight tabular-nums" data-testid="overview-kpi-rooms-value">
                  {current_status.total_rooms}
                </p>
                <p className="text-xs text-muted-foreground mt-2">{current_status.clean_rooms} clean</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <BedDouble className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-[0_10px_30px_rgba(15,23,42,0.06)] hover:shadow-[0_16px_40px_rgba(15,23,42,0.10)] transition-shadow duration-200" data-testid="overview-kpi-confirmed-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Confirmed</p>
                <p className="text-2xl md:text-3xl heading-font tracking-tight tabular-nums" data-testid="overview-kpi-confirmed-value">
                  {bookings.confirmed}
                </p>
                <p className="text-xs text-muted-foreground mt-2">upcoming stays</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl border bg-card shadow-[0_10px_30px_rgba(15,23,42,0.06)] hover:shadow-[0_16px_40px_rgba(15,23,42,0.10)] transition-shadow duration-200" data-testid="overview-kpi-checkedin-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Checked In</p>
                <p className="text-2xl md:text-3xl heading-font tracking-tight tabular-nums" data-testid="overview-kpi-checkedin-value">
                  {bookings.checked_in}
                </p>
                <p className="text-xs text-muted-foreground mt-2">{current_status.current_occupancy_pct}% occupied</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--status-occupied))]/10 flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-5 h-5 text-[hsl(var(--status-occupied))]" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Trends Chart - md:col-span-8 */}
        <Card className="md:col-span-8" data-testid="overview-revenue-trends-chart">
          <CardHeader>
            <CardTitle className="text-lg heading-font tracking-tight">Revenue & Occupancy Trends</CardTitle>
            <CardDescription>Last 7 days performance</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.14}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#revenueGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Requests Feed - md:col-span-4 */}
        <Card className="md:col-span-4" data-testid="overview-active-requests-feed">
          <CardHeader>
            <CardTitle className="text-lg heading-font tracking-tight flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" />
              Active Requests
            </CardTitle>
            <CardDescription>{pendingRequests.length} pending</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              {pendingRequests.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
                  <p className="text-sm text-muted-foreground">No pending requests</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingRequests.map(req => (
                    <div key={req.request_id} className="p-3 rounded-lg border bg-card hover:bg-accent/5 transition-colors" data-testid={`overview-request-${req.request_id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <Badge variant={req.priority === 'urgent' ? 'destructive' : 'outline'} className="text-[10px]">
                          {req.priority}
                        </Badge>
                        <span className="mono text-xs text-muted-foreground">Rm {req.room_number}</span>
                      </div>
                      <p className="text-sm font-medium line-clamp-2">{req.issue}</p>
                      <p className="text-xs text-muted-foreground mt-1">{req.category}</p>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Property Snapshot - md:col-span-4 */}
        <Card className="md:col-span-4" data-testid="overview-property-info-card">
          <CardHeader>
            <CardTitle className="text-lg heading-font tracking-tight">Property Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Hotel className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">{hotelName}</p>
                <p className="text-xs text-muted-foreground">{hotels?.length || 0} properties total</p>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant="default">Active</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Verification</span>
                <Badge variant="secondary" className="bg-[hsl(var(--status-clean))]/10 text-[hsl(var(--status-clean))]">Verified</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Trust Score</span>
                <span className="font-semibold mono">92/100</span>
              </div>
            </div>

            <Separator />

            <div className="bg-[hsl(var(--surface-2))] rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Booking Link</p>
              <p className="text-xs mono break-all text-foreground/80">{PREVIEW_URL}/book/{hotelId}</p>
            </div>
          </CardContent>
        </Card>

        {/* Occupancy Breakdown - md:col-span-8 */}
        <Card className="md:col-span-8">
          <CardHeader>
            <CardTitle className="text-lg heading-font tracking-tight">Room Status Distribution</CardTitle>
            <CardDescription>Current operational breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-lg border border-[hsl(var(--status-occupied))]/20 bg-[hsl(var(--status-occupied))]/5">
                <p className="text-3xl font-bold heading-font text-[hsl(var(--status-occupied))]">{current_status.inhouse_guests}</p>
                <p className="text-xs text-muted-foreground mt-1">In-House</p>
              </div>
              <div className="text-center p-4 rounded-lg border border-[hsl(var(--status-clean))]/20 bg-[hsl(var(--status-clean))]/5">
                <p className="text-3xl font-bold heading-font text-[hsl(var(--status-clean))]">{current_status.clean_rooms}</p>
                <p className="text-xs text-muted-foreground mt-1">Clean</p>
              </div>
              <div className="text-center p-4 rounded-lg border border-[hsl(var(--status-dirty))]/20 bg-[hsl(var(--status-dirty))]/5">
                <p className="text-3xl font-bold heading-font text-[hsl(var(--status-dirty))]">{current_status.dirty_rooms}</p>
                <p className="text-xs text-muted-foreground mt-1">Dirty</p>
              </div>
              <div className="text-center p-4 rounded-lg border border-[hsl(var(--status-maintenance))]/20 bg-[hsl(var(--status-maintenance))]/5">
                <p className="text-3xl font-bold heading-font text-[hsl(var(--status-maintenance))]">{current_status.maintenance_rooms}</p>
                <p className="text-xs text-muted-foreground mt-1">Maintenance</p>
              </div>
            </div>

            <Separator className="my-6" />

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Occupancy Rate</p>
                <p className="text-4xl font-bold heading-font tabular-nums">{current_status.current_occupancy_pct}%</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Average Daily Rate</p>
                <p className="text-4xl font-bold heading-font tabular-nums mono">${revenue.adr}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Operational Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card data-testid="overview-bookings-detail-card">
          <CardHeader>
            <CardTitle className="text-lg heading-font tracking-tight">Booking Details</CardTitle>
            <CardDescription>Period summary</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground">Total Bookings</span>
              <span className="font-bold tabular-nums">{bookings.total}</span>
            </div>
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground">Checked In</span>
              <span className="font-semibold text-[hsl(var(--status-occupied))] tabular-nums">{bookings.checked_in}</span>
            </div>
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground">Checked Out</span>
              <span className="font-semibold tabular-nums">{bookings.checked_out}</span>
            </div>
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground">Confirmed</span>
              <span className="font-semibold text-primary tabular-nums">{bookings.confirmed}</span>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="overview-revenue-detail-card">
          <CardHeader>
            <CardTitle className="text-lg heading-font tracking-tight">Revenue Breakdown</CardTitle>
            <CardDescription>Period performance</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground">Total Revenue</span>
              <span className="font-bold mono tabular-nums">${revenue.total_revenue}</span>
            </div>
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground">ADR</span>
              <span className="font-semibold mono tabular-nums">${revenue.adr}</span>
            </div>
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground">RevPAR</span>
              <span className="font-semibold mono tabular-nums">${revenue.revpar}</span>
            </div>
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                RevPAR Growth
                <TrendingUp className="w-3 h-3 text-[hsl(var(--kpi-positive))]" />
              </span>
              <span className="font-semibold text-[hsl(var(--kpi-positive))] tabular-nums">+8%</span>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="overview-occupancy-detail-card">
          <CardHeader>
            <CardTitle className="text-lg heading-font tracking-tight">Occupancy Metrics</CardTitle>
            <CardDescription>Utilization stats</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground">Period Occupancy</span>
              <span className="font-bold tabular-nums">{occupancy.period_occupancy_pct}%</span>
            </div>
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground">Room Nights Sold</span>
              <span className="font-semibold tabular-nums">{occupancy.total_room_nights_sold}</span>
            </div>
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground">Available Nights</span>
              <span className="font-semibold tabular-nums">{occupancy.available_room_nights}</span>
            </div>
            <div className="flex justify-between items-center p-2 rounded-lg hover:bg-accent/5 transition-colors">
              <span className="text-sm text-muted-foreground">Utilization Rate</span>
              <span className="font-semibold text-primary tabular-nums">
                {occupancy.available_room_nights > 0 
                  ? Math.round((occupancy.total_room_nights_sold / occupancy.available_room_nights) * 100)
                  : 0}%
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
